import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  keyframes,
  Icon,
  Divider,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { CommentsList } from "src/components/upgrades/CommentsList.js";
import User2 from "src/assets/users/user2.png";

// Pulsing animation for calendar icon
const pulse = keyframes`
  0%, 100% { 
    transform: scale(1);
    opacity: 1;
  }
  50% { 
    transform: scale(1.05);
    opacity: 0.8;
  }
`;

// Erasing animation - mask moving from right to left
const eraseText = keyframes`
  0% { 
    clip-path: inset(0 0 0 0);
  }
  100% { 
    clip-path: inset(0 100% 0 0);
  }
`;

// Fade in animation
const fadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

// Pulse animation for "near real-time" text
const textPulse = keyframes`
  0%, 100% { 
    opacity: 1;
    transform: scale(1);
  }
  50% { 
    opacity: 0.8;
    transform: scale(1.02);
  }
`;

const TransparentUpdatesExplainer = ({
  onUpgradeClick,
  onSeeAllBenefits,
  sx,
  ...props
}) => {
  const [showStrikethrough, setShowStrikethrough] = useState(false);
  const [animationStep, setAnimationStep] = useState(0);
  const theme = useTheme();

  // Get today's date
  const today = new Date();
  const dayName = today.toLocaleDateString("en-US", { weekday: "long" });
  const dayNumber = today.getDate();

  // Animation sequence
  useEffect(() => {
    const sequence = async () => {
      // Step 0: Reset to calendar view
      setAnimationStep(0);
      setShowStrikethrough(false);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Step 1: Show strikethrough and new text
      setShowStrikethrough(true);
      await new Promise((resolve) => setTimeout(resolve, 4000)); // Increased from 2000 to 4000

      // Step 2: Show calendar event view
      setAnimationStep(1);
      await new Promise((resolve) => setTimeout(resolve, 6000)); // Increased from 3000 to 6000

      // Reset and restart
      setAnimationStep(0);
      setShowStrikethrough(false);
    };

    const interval = setInterval(sequence, 11000); // Increased from 10000 to 13000
    sequence(); // Start immediately

    return () => clearInterval(interval);
  }, []);

  const CalendarIconWithProgress = () => (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: "90px",
          height: "90px",
          borderRadius: "24px",
          backgroundColor: "white",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          animation: `${pulse} 2s ease-in-out infinite`,
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
          border: "1px solid rgba(0, 0, 0, 0.04)",
        }}
      >
        {/* Day name */}
        <Typography
          sx={{
            fontSize: "12px",
            color: "#FF3B30",
            fontWeight: 500,
            textTransform: "uppercase",
            lineHeight: 1,
            marginBottom: "6px",
            letterSpacing: "0.5px",
          }}
        >
          {dayName.slice(0, 3)}
        </Typography>

        {/* Day number */}
        <Typography
          sx={{
            fontSize: "46px",
            color: "#000",
            fontWeight: 300,
            lineHeight: 0.8,
          }}
        >
          {dayNumber}
        </Typography>
      </Box>
    </Box>
  );

  const CalendarEventView = () => {
    const [showLastUpdated, setShowLastUpdated] = useState(false);
    const [showRainDelay, setShowRainDelay] = useState(false);
    const [showBottomText, setShowBottomText] = useState(false);

    useEffect(() => {
      const sequence = async () => {
        // Reset states
        setShowLastUpdated(false);
        setShowRainDelay(false);
        setShowBottomText(false);

        // Show "Last updated" after initial fade in
        await new Promise((resolve) => setTimeout(resolve, 800));
        setShowLastUpdated(true);

        // Show "Rain delay" after "Last updated"
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setShowRainDelay(true);

        // Show bottom text after rain delay
        await new Promise((resolve) => setTimeout(resolve, 1200));
        setShowBottomText(true);
      };

      sequence();
    }, []);

    return (
      <Box
        sx={{
          width: "85%",
          maxWidth: "220px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          animation: `${fadeIn} 0.8s ease-out`,
          padding: "0 16px",
          mb: 1,
        }}
      >
        {/* Event details with edge lines */}
        <Box sx={{ position: "relative" }}>
          {/* Left edge line */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "1px",
              height: "100%",
              backgroundColor: theme.palette.overlay.black15,
            }}
          />
          {/* Right edge line */}
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: "1px",
              height: "100%",
              backgroundColor: theme.palette.overlay.black15,
            }}
          />
          {/* Event Details Section */}
          <Box sx={{ flex: 1, mt: 2, px: 2 }}>
            {/* Location Row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                mb: "12px",
              }}
            >
              <Icon
                baseClassName="material-symbols-outlined"
                sx={{
                  fontSize: "16px",
                  color: theme.palette.overlay.black60,
                  opacity: 0.8,
                  fontWeight: 300,
                  minWidth: "16px",
                }}
              >
                location_on
              </Icon>
              <Box
                sx={{
                  flex: 1,
                  height: "8px",
                  backgroundColor: theme.palette.overlay.black20,
                  borderRadius: "4px",
                }}
              />
            </Box>

            {/* Description Section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                mb: "12px",
              }}
            >
              <Icon
                baseClassName="material-symbols-outlined"
                sx={{
                  fontSize: "16px",
                  color: theme.palette.overlay.black60,
                  opacity: 0.8,
                  fontWeight: 300,
                  minWidth: "16px",
                  mt: "-2px",
                }}
              >
                notes
              </Icon>
              <Box sx={{ flex: 1 }}>
                {/* First description line */}
                <Box
                  sx={{
                    height: "8px",
                    backgroundColor: theme.palette.overlay.black20,
                    borderRadius: "4px",
                    mb: "6px",
                    width: "100%",
                  }}
                />

                {/* "Last updated" timestamp - animated */}
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "6px",
                    padding: "4px 4px",
                    border: `1px solid ${theme.palette.overlay.black20}`,
                    mb: "4px",
                    opacity: showLastUpdated ? 1 : 0,
                    animation: showLastUpdated
                      ? `${fadeIn} 0.6s ease-out`
                      : "none",
                    transition: "opacity 0.6s ease-out",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.primary.main,
                      lineHeight: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "3px",
                      fontSize: "11px",
                    }}
                  >
                    <Icon
                      baseClassName="material-symbols-outlined"
                      sx={{
                        fontSize: "12px",
                        fontWeight: 300,
                      }}
                    >
                      schedule
                    </Icon>
                    Last updated: 2 hrs ago
                  </Typography>
                </Box>

                {/* Rain delay alert - animated */}
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "6px",
                    padding: "4px 4px",
                    border: `1px solid ${theme.palette.overlay.black20}`,
                    opacity: showRainDelay ? 1 : 0,
                    animation: showRainDelay
                      ? `${fadeIn} 0.6s ease-out`
                      : "none",
                    transition: "opacity 0.6s ease-out",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.primary.main,
                      lineHeight: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "3px",
                      fontSize: "11px",
                    }}
                  >
                    <Icon
                      baseClassName="material-symbols-outlined"
                      sx={{
                        fontSize: "12px",
                        fontWeight: 300,
                        color: theme.palette.primary.main,
                      }}
                    >
                      warning
                    </Icon>
                    Rain delay of 30 minutes
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* Bottom text - animated */}
        <Box>
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              textAlign: "center",
              color: theme.palette.text.secondary,
              fontWeight: 300,
              opacity: showBottomText ? 1 : 0,
              animation: showBottomText ? `${fadeIn} 0.6s ease-out` : "none",
              transition: "opacity 0.6s ease-out",
            }}
          >
            All changes,{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: theme.palette.primary.main,
                animation: `${fadeIn} 0.6s ease-out 1s both, ${textPulse} 2s ease-in-out 1.6s infinite`,
              }}
            >
              time-stamped.
            </Box>{" "}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        textAlign: "center",
        margin: "0 auto",
        width: "100%",
        ...sx,
      }}
      {...props}
    >
      {/* Title */}
      <Box
          sx={{
            fontSize: "3rem",
            color: theme.palette.text.secondary,
            fontFamily: "Material Symbols Outlined",
            fontWeight: 100,
            display: "block",
            margin: "0 auto",
          }}
        >
          bolt
      </Box>
      <Typography
        variant="title"
        sx={{
          mb: 3,
          color: theme.palette.text.primary,
          lineHeight: 1.2,
          fontWeight: 300,
        }}
      >
        Your Calendar.{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          Upgraded.
        </Box>
      </Typography>

      {/* Animation Area */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "220px",
          my: 2,
          borderRadius: "12px",
          backgroundColor: theme.palette.smokyWhite,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          transition: "all 0.6s ease-in-out",
        }}
      >
        {/* Step 0: Calendar Icon with text animation */}
        {animationStep === 0 && (
          <>
            <CalendarIconWithProgress />

            {/* Update frequency text */}
            <Box sx={{ mt: 3, position: "relative", height: "20px" }}>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {/* "Updates " part - always visible */}
                <Typography
                  sx={{
                    variant: "body1",
                    color: theme.palette.text.primary,
                    fontWeight: 300,
                    marginRight: "4px",
                  }}
                >
                  Updates
                </Typography>

                {/* Changeable part */}
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  {!showStrikethrough && (
                    <Typography
                      sx={{
                        variant: "body1",
                        color: theme.palette.text.primary,
                        fontWeight: 300,
                      }}
                    >
                      every 24-48hrs.
                    </Typography>
                  )}

                  {showStrikethrough && (
                    <Box sx={{ position: "relative", display: "inline-block" }}>
                      {/* Erasing text */}
                      <Typography
                        sx={{
                          variant: "body1",
                          color: theme.palette.text.primary,
                          fontWeight: 300,
                          whiteSpace: "nowrap",
                          animation: `${eraseText} 1s ease-out forwards`,
                          animationFillMode: "forwards",
                        }}
                      >
                        every 24-48hrs
                      </Typography>

                      {/* Replacement text */}
                      <Typography
                        sx={{
                          variant: "body1",
                          color: theme.palette.text.primary,
                          fontWeight: 600,
                          position: "absolute",
                          top: 0,
                          left: 0,
                          whiteSpace: "nowrap",
                          opacity: 0,
                          animation: `${fadeIn} 0.6s ease-out 1s both, ${textPulse} 2s ease-in-out 1.6s infinite`,
                        }}
                      >
                        near real-time.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </>
        )}

        {/* Step 1: Calendar Event View */}
        {animationStep === 1 && <CalendarEventView />}
      </Box>
    </Box>
  );
};

export default TransparentUpdatesExplainer;
